import * as pdfFonts from "pdfmake/build/vfs_fonts"

import { ActivatedRoute } from "@angular/router"
import { Component } from "@angular/core"
import { OnInit } from "@angular/core"
import { PipeFmt } from "app/pipes/fmt"
import type { RetrieveResponse as Report } from "app/services/report"
import { ServiceReport } from "app/services/report"
import { ServiceReportFile } from "app/services/report-file"
import { ServiceReportImage } from "app/services/report-image"
import { ServiceUser } from "app/services/user"
import type { TDocumentDefinitions } from "pdfmake/interfaces"
import { ToastrService } from "ngx-toastr"
import { createPdf } from "pdfmake/build/pdfmake"

@Component({ providers: [PipeFmt], styleUrls: ["index.scss"], templateUrl: "index.html" })
export class RouteReportComponent implements OnInit {
  loading = false

  report: Report | null = null

  user = this.serviceUser.current.getValue()

  constructor(
    private fmt: PipeFmt,
    private route: ActivatedRoute,
    private serviceReport: ServiceReport,
    private serviceReportFile: ServiceReportFile,
    private serviceReportImage: ServiceReportImage,
    private serviceUser: ServiceUser,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.route.params.subscribe({
      next: ({ reportId: id }) => {
        this.loading = true
        this.serviceReport.retrieve(id).subscribe({
          next: (resp: any) => {
            this.loading = false
            if (resp.images && Array.isArray(resp.images)) {
              resp.images.sort((a: any, b: any) => a.order - b.order);
            }

            this.report = resp;
          },
          error: (resp) => {
            this.loading = false
            this.toastr.error("We will investigate the problem.", `${resp.statusText}`)
            throw resp
          },
        })
      },
    })
  }

  onFilesClick(action: "download", file: Report["files"][number]) {
    if (action === "download") {
      file.$loading = true
      this.serviceReportFile.download(file.id).subscribe({
        next: () => {
          file.$loading = false
        },
        error: (resp) => {
          file.$loading = false
          this.toastr.error("We will investigate the problem.", `${resp.statusText}`)
          throw resp
        },
      })
    }
  }

  onImagesClick(action: "download" | "view", image: Report["images"][number]) {
    if (action === "download") {
      image.$loading = true
      this.serviceReportImage.download(image.id).subscribe({
        next: () => {
          image.$loading = false
        },
        error: (resp) => {
          image.$loading = false
          this.toastr.error("We will investigate the problem.", `${resp.statusText}`)
          throw resp
        },
      })
    }
    if (action === "view") {
      image.$loading = true
      this.serviceReportImage.view(image.id).subscribe({
        next: () => {
          image.$loading = false
        },
        error: (resp) => {
          image.$loading = false
          this.toastr.error("We will investigate the problem.", `${resp.statusText}`)
          throw resp
        },
      })
    }
  }

  onReportClick(action: "pdf-download" | "pdf-view") {
    if (this.report) {
      this.pdfMake(this.report).then(({ fname, pdf }) => {
        if (action === "pdf-download") {
          pdf.download(fname)
        }
        if (action === "pdf-view") {
          pdf.open()
        }
      })
    }
  }

  // Add this as a class method with improved BMP detection and error handling
async convertBmpToPng(imageData: string): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      
      // Set up error handling
      img.onerror = () => {
        resolve(imageData); // Return original on error
      };
      
      img.onload = () => {
        try {
          // Create a canvas to draw the image
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the BMP onto the canvas
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            
            // Convert to PNG with maximum quality
            const pngData = canvas.toDataURL('image/png', 1.0);
            resolve(pngData);
          } else {
            console.error("Could not get canvas context");
            resolve(imageData); // Return original if context is null
          }
        } catch (canvasError) {
          resolve(imageData); // Return original on canvas error
        }
      };
      
      // Set source of image to the BMP data
      img.src = imageData;
    } catch (error) {
      resolve(imageData); // Return original on any other error
    }
  });
}

// Keep your existing RouteReportComponent class but add these methods:

// Map of template names to template creator functions
private templateGenerators : any= {
  'default': this.createDefaultTemplate.bind(this),
  '1': this.createOneTemplate.bind(this),
  '2': this.createTwoTemplate.bind(this),
  '3': this.createThreeTemplate.bind(this)
};

async pdfMake(report: Report) {
  const { fmt, user } = this;

  const templateID : any= localStorage.getItem('currentTemplateID');
  
  const templateName = `${templateID}`;
  const templateGenerator = this.templateGenerators[templateName] || this.templateGenerators['default'];
  
  const content: any = await templateGenerator(report);
  
  const title = `Referral Report - ${report.patient.first_name} ${report.patient.last_name}`;
  
  // Template-specific document configurations
  let docDefinition: TDocumentDefinitions = {
    content,
    defaultStyle: { fontSize: 11 },
    info: { author: "DentalEMR Inc", title, keywords: `report-id=${report.id} user-id=${user.id}` },
    pageOrientation: "portrait",
    pageSize: "A4",
  };
  
if (templateName === '3') {
  docDefinition.footer = (currentPage, pageCount) => {
    return {
      stack: [
        // This will be your absolutely positioned box
        {
          absolutePosition: { x: 0, y: 0 }, // Position relative to footer
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: -10,
              w: 595.28, // Full page width
              h: 30, // Height of your box
              color: '#F0F0F0', // Light gray background
            }
          ]
        },
        {
          columns: [
            { 
              text: `${report.practice.location.street1} | ${report.practice.location.street2} ${report.practice.location.street2 ? '|' : ''} ${report.practice.location.city} | ${report.practice.location.state} | ${report.practice.location.zip_code} | ${report.practice.phone}`,
              alignment: 'center',
              fontSize: 10
            },
          ],
          relativePosition: { x: 20, y: 0 },
        }
      ],
      margin: [0, 0, 0, 0], // Remove any default margins
    };
  };
}
  
  return {
    fname: `${title}.pdf`,
    pdf: createPdf(
      docDefinition,
      undefined,
      undefined,
      pdfFonts.pdfMake.vfs,
    ),
  };
}

// Your current template logic moved to a dedicated method
async createDefaultTemplate(report: Report) {
  const { fmt } = this;
  const content = [];
  
  const {
    id,
    close_message,
    description,
    images,
    patient: {
      first_name,
      last_name,
      birth_date,
      mugshot,
    },
    practice: {
      picture: practicePicture = "",
      name,
      location: {
        street1,
        street2,
        city,
        state,
        zip_code,
      },
      phone,
    },
    sent_at,
    signing_provider: {
      id: provider_id,
      signature: providerSignature,
      first_name: provider_first_name,
      last_name: provider_last_name,
    },
    summary,
  } = report;
  
  // Process practice picture if it exists
  const processedPracticePicture = practicePicture ? await this.processImage(practicePicture) : practicePicture;
  
  content.push({
    columns: [
      {
        image: fmt.transform(processedPracticePicture, "b64-img"),
        width: 80,
      },
      {
        ul: [
          {
            text: fmt.transform(name),
            style: {
              bold: true,
              fontSize: 12,
            },
          },
          {
            text: fmt.transform(street1),
          },
          {
            text: fmt.transform(street2),
          },
          {
            text: `${fmt.transform(city)}, ${fmt.transform(state)} ${fmt.transform(zip_code)}`,
          },
          {
            text: fmt.transform(phone, "phone-number"),
          },
        ],
        type: "none",
        width: "*", 
      },
      {
        ul: [
          {
            text: "Patient Name",
            style: {
              bold: true,
            },
          },
          {
            text: `${fmt.transform(last_name)}, ${fmt.transform(first_name)}`,
          },
          {
            text: "Patient DOB",
            margin: [0, 6, 0, 0],
            style: {
              bold: true,
            },
          },
          {
            text: fmt.transform(birth_date, "date"),
          },
        ],
        style: {
          alignment: "right",
        },
        type: "none",
        width: "auto",
      },
    ],
    columnGap: 6,
  })
  content.push({
    text: [
      {
        text: "Published: ",
        style: {
          bold: true,
          fontSize: 12,
        },
      },
      {
        text: fmt.transform(sent_at, "date"),
      },
    ],
    margin: [0, 5],
  })
  if (summary) {
    content.push({
      text: [
        {
          text: "Re: ",
          style: {
            fontSize: 12,
          },
        },
        {
          text: fmt.transform(summary),
        },
      ],
      margin: [0, 5],
      style: {
        bold: true,
      },
    })
  }
  if (description) {
    content.push({
      text: fmt.transform(description),
      margin: [0, 5],
      style: {
        preserveLeadingSpaces: true, // preserveTrailingSpaces: true,
      },
    })
  }
  if (images.length > 0) {
    await Promise.all(
        images.map(async ({ name: text, picture }) => {
          // Process each image, converting BMP if necessary
          const imageDataUrl = await picture.dataurl();
          const processedImage = await this.processImage(imageDataUrl);
          return { text, image: processedImage };
        })
      ).then(
        (processedImages) => {
          processedImages
            .reduce((memo, item, idx) => {
              if (idx % 3 === 0) {
                memo.push([])
              }
              memo[memo.length - 1].push(item)
              return memo
            }, <typeof processedImages[number][][]>[])
            .forEach((row) => {
              content.push({
                columns: row.map(({ text, image }) => ({
                  stack: [
                    {
                      image: fmt.transform(image, "b64-img"),
                      width: 160,
                    },
                    {
                      text: fmt.transform(text),
                      maxHeight: 26,
                    },
                  ],
                  width: "*",
                  unbreakable: true,
                })),
                margin: [0, 5],
                style: {
                  alignment: "center",
                },
              })
            })
        },
      )
    }
    if (close_message) {
      content.push({
        text: fmt.transform(close_message),
        margin: [0, 5],
        style: {
          preserveLeadingSpaces: true, // preserveTrailingSpaces: true,
        },
      })
    }
    if (provider_id) {
      // Process provider signature if it exists
      const processedProviderSignature = providerSignature ? await this.processImage(providerSignature) : providerSignature;

      content.push({
        image: fmt.transform(processedProviderSignature, "b64-img"),
        fit: [300, 155],
        margin: [0, 5],
      })
      content.push({
        columns: [
          {
            text: `${fmt.transform(provider_last_name)}, ${fmt.transform(provider_first_name)}`,
            width: 300,
            style: {
              alignment: "center",
            },
          },
        ],
        margin: [0, 5],
      })
    }
  
  return content;
}

// New template: One design
async createOneTemplate(report: Report) {
  const { fmt } = this;
  const content = [];

  const {
    id,
    close_message,
    description,
    images,
    patient: {
      first_name,
      last_name,
      birth_date,
      mugshot,
    },
    practice: {
      picture: practicePicture = "",
      name,
      location: {
        street1,
        street2,
        city,
        state,
        zip_code,
      },
      phone,
    },
    sent_at,
    signing_provider: {
      id: provider_id,
      signature: providerSignature,
      first_name: provider_first_name,
      last_name: provider_last_name,
    },
    summary,
  } = report;

  // Safely get updated_at if present
  const updated_at = (report as any).updated_at || null;
  
  // Process practice picture if it exists
  const processedPracticePicture = practicePicture ? await this.processImage(practicePicture) : practicePicture;
  const processedMugshot = mugshot ? await this.processImage(mugshot) : mugshot;
  
  // Header with background and structured layout (matching NewReportPDF.getHeaderContent)
  content.push({
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
    },
    style: {
      fontSize: 11,
      color: '#000000',
    },
    table: {
      body: [[
        processedPracticePicture
          ? { 
              image: fmt.transform(processedPracticePicture, "b64-img"), 
              width: 130, 
              margin: [40, 10, 0, 10], 
              absolutePosition: { x: 20, y: 25 } 
            }
          : { text: "", width: 130 },
        { text: "", width: "*" },
        {
          text: [
            { 
              text: fmt.transform(name), 
              style: ["bold", "header"], 
              color: '#555555', 
              fontSize: 12,
              bold: true
            },
            "\n",
            { 
              text: fmt.transform(street1), 
              color: '#555555' 
            },
            (street2 ? " " + fmt.transform(street2) : ""),
            "\n",
            {
              text: `${fmt.transform(city)}, ${fmt.transform(state)} ${fmt.transform(zip_code)}`,
              color: '#555555'
            },
            "\n",
            {
              text: fmt.transform(phone, "phone-number"),
              color: '#555555',
            }
          ],
          alignment: 'right',
          margin: [0, 10, 20, 10],
        },
        { text: "", width: 50 }
      ]],
      widths: ['*', '*', 200, 50],
    },
    fillColor: '#f1f1f1',
    width: '100%',
    margin: [-40, 0, 0, 0],
  });

  // Patient content section (matching NewReportPDF.getPatientContent)
  const patientName = `${fmt.transform(first_name)} ${fmt.transform(last_name)}`;
  const dobFormatted = birth_date ? fmt.transform(birth_date, "date") : "N/A";
  
  content.push({
    margin: [0, 30, 10, 0],
    layout: "noBorders",
    style: {
      fontSize: 12,
      color: '#333333',
    },
    table: {
      widths: ["*", "auto", "auto"], 
      body: [
        [
          { text: "" },
          { 
            stack: [
              { text: patientName, bold: true },
              { text: `DOB: ${dobFormatted}` },
            ],
            alignment: "right",
            margin: [0, 5, 10, 0]
          },
          processedMugshot ? {
            image: fmt.transform(processedMugshot, "b64-img"),
            width: 60,
            alignment: "right",
          } : { text: "", width: 60 }
        ]
      ]
    }
  });

  // Summary and description content (refactored to match NewReportPDF.getSummaryAndDescriptionContent)
  if (sent_at) {
    content.push({
      text: `Published: ${fmt.transform(sent_at, "date")}`,
      fontSize: 10,
      color: '#4A90E2',
      margin: [-20, !mugshot ? 25 : -5, 0, 0]
    });
  }

  if (sent_at) {
    content.push({
      text: fmt.transform(sent_at, "date"),
      fontSize: 12,
      color: '#333333',
      absolutePosition: { x: 20, y: 160 }
    });
  }

  if (summary) {
    content.push({
      text: `Re: ${fmt.transform(summary)}`,
      fontSize: 12,
      bold: true,
      color: '#333333',
      absolutePosition: { x: 20, y: 185 }
    });
  }

  if (description) {
    content.push({
      text: fmt.transform(description),
      fontSize: 11,
      color: '#555555',
      margin: [
        -20,
        10,
        0,
        20
      ],
      lineHeight: 1.5,
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true
    });
  }

  // Images content (matching NewReportPDF.getImagesContent layout)
  if (images && images.length > 0) {
    const processedImages = await Promise.all(
      images.map(async ({ name: text, picture }) => {
        const imageDataUrl = await picture.dataurl();
        const processedImage = await this.processImage(imageDataUrl);
        return { text: fmt.transform(text), image: processedImage };
      })
    );

    // Group images in rows of 3 (chunk function equivalent)
    const imageRows = processedImages.reduce((memo, item, idx) => {
      if (idx % 3 === 0) {
        memo.push([]);
      }
      memo[memo.length - 1].push(item);
      return memo;
    }, [] as typeof processedImages[0][][]);

    imageRows.forEach((row) => {
      const imageRow = [{ text: "" }]; // left spacing
      
      for (let i = 0; i < 3; i++) {
        if (i < row.length) {
          imageRow.push({
            stack: [
              { 
                image: fmt.transform(row[i].image, "b64-img"), 
                fit: [160, 160], 
                alignment: "center", 
                width: 160 
              },
              { 
                text: row[i].text, 
                style: "a_center", 
                width: 160, 
                alignment: "center", 
                noWrap: false, 
                margin: [0, 8, 0, 0] 
              }
            ],
            width: 160,
            alignment: "center",
          } as any); // pdfmake expects cell objects, so cast as any to avoid TS error
        } else {
          imageRow.push({ text: "" });
        }
      }
      
      imageRow.push({ text: "" }); // right spacing

      content.push({
        layout: "noBorders",
        margin: [0, 0, 0, 10],
        unbreakable: true,
        style: {
          fontSize: 8
        },
        table: {
          body: [imageRow],
          widths: ["*", 160, 160, 160, "*"], // flexible columns for spacing
        }
      });
    });
  }

  // Close message content (matching NewReportPDF.getCloseMessageContent)
  if (close_message) {
    content.push({
      text: fmt.transform(close_message),
      fontSize: 11,
      lineHeight: 1.5,
      color: '#4A5568',
      margin: [-20, 10, 0, 10],
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true,
    });
  }

  // Signing provider content (matching NewReportPDF.getSigningProviderContent)
    const processedProviderSignature = providerSignature ? await this.processImage(providerSignature) : null;
    const providerName = `${fmt.transform(provider_first_name)} ${fmt.transform(provider_last_name)}`.trim();
    const signatureDate = (report as any)?.sent_at ? fmt.transform((report as any).sent_at, "date") : "";

    const placeholderImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

    content.push({
      unbreakable: true,
      style: {
        fontSize: 9
      },
      margin: [-20, 0, 0, 0],
      table: {
        widths: [150, 50, 150],
        body: [
          [
            {
              stack: [
                {
                  image: processedProviderSignature ? fmt.transform(processedProviderSignature, "b64-img") : placeholderImage,
                  fit: processedProviderSignature ? [150, 75] : [100, 50],
                  alignment: "left",
                },
                {
                  text: providerName,
                  alignment: "left",
                  margin: [0, 5, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              stack: [
                {
                  text: signatureDate,
                  alignment: "left",
                  margin: [0, 80, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
          ],
          [
            {
              text: "Doctor Signature Line",
              alignment: "left",
              border: [false, true, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "Doctor Signature Date",
              alignment: "left",
              border: [false, true, false, false],
            }
          ]
        ]
      }
    });

  return content;
}

// Add more template methods as needed
async createTwoTemplate(report: Report) {
  const { fmt } = this;
  const content = [];
  
  const {
    id,
    close_message,
    description,
    images,
    patient: {
      first_name,
      last_name,
      birth_date,
      mugshot,
    },
    practice: {
      picture: practicePicture = "",
      name,
      location: {
        street1,
        street2,
        city,
        state,
        zip_code,
      },
      phone,
    },
    sent_at,
    signing_provider: {
      id: provider_id,
      signature: providerSignature,
      first_name: provider_first_name,
      last_name: provider_last_name,
    },
    summary,
  } = report;

  // Safely get updated_at if present
  const updated_at = (report as any).updated_at || null;
  
  // Process images
  const processedPracticePicture = practicePicture ? await this.processImage(practicePicture) : practicePicture;
  const processedMugshot = mugshot ? await this.processImage(mugshot) : mugshot;
  
  // Helper function to build city, state, zip without extra spaces (from NewReportPDF2)
  const buildCityStateZip = () => {
    let result = "";
    const cityVal = fmt.transform(city);
    const stateVal = fmt.transform(state);
    const zipVal = fmt.transform(zip_code);
    
    if (cityVal) result += cityVal;
    if (stateVal) result += (cityVal ? ", " : "") + stateVal;
    if (zipVal) result += (cityVal || stateVal ? " " : "") + zipVal;
    
    return result;
  };
  
  // Header with background and structured layout (matching NewReportPDF2.getHeaderContent)
  content.push({
    stack: [
      {
        canvas: [{
          type: 'rect',
          x: 0,
          y: 0,
          w: 595,
          h: 150,
          color: '#f1f1f1'
        }],
        absolutePosition: { x: 0, y: 0 }
      },
      {
        columns: [
          {
            width: 140,
            stack: [
              processedPracticePicture ? {
                image: fmt.transform(processedPracticePicture, "b64-img"),
                width: 90,
                margin: [40, 40, 0, 0]
              } : { text: "", height: 50, margin: [0, 40, 0, 0] }
            ]
          },  
          {
            width: '*',
            text: '',
            margin: [0, 0, 0, 0]
          },
          {
            width: 250,
            stack: [{
              text: [
                { text: fmt.transform(name), style: "practiceHeader" },
                fmt.transform(street1) ? "\n" : "",
                { text: fmt.transform(street1), style: "practiceText" },
                fmt.transform(street2) ? "\n" : "",
                { text: fmt.transform(street2), style: "practiceText" },
                buildCityStateZip() ? "\n" : "",
                { text: buildCityStateZip(), style: "practiceText" },
                fmt.transform(phone, "phone-number") ? "\n" : "",
                { text: fmt.transform(phone, "phone-number"), style: "practiceText" }
              ],
              alignment: 'right',
              margin: [0, 40, 20, 0]
            }]
          }
        ],
        absolutePosition: { x: 0, y: 0 },
        columnGap: 0,
        margin: [0, 0, 0, 20]
      }
    ],
    styles: {
      practiceHeader: {
        bold: true,
        fontSize: 14,
        color: '#D27D2D'
      },
      practiceText: {
        fontSize: 11,
        color: '#555555'
      }
    },
    margin: [0, 0, 0, 80]
  });

  // Patient content section (matching NewReportPDF2.getPatientContent)
  const patientName = `${fmt.transform(first_name)} ${fmt.transform(last_name)}`.trim();
  const dobFormatted = birth_date ? fmt.transform(birth_date, "date") : "N/A";
  const updatedDate = sent_at ? fmt.transform(sent_at, "date") : "";
  
  content.push({
    layout: "noBorders",
    style: { fontSize: 12, color: '#333333' },
    margin: [0, 40, 0, 0],
    table: {
      widths: ["*", "auto"],
      body: [[
        {
          layout: "noBorders",
          table: {
            widths: ["auto", "auto"],
            body: [[
              {
                stack: [
                  { text: patientName, bold: true, fontSize: 14, margin: [0, 0, 0, 5] },
                  { text: `DOB: ${dobFormatted}` }
                ],
                margin: [0, 0, 10, 0],
              },
              processedMugshot ? {
                image: fmt.transform(processedMugshot, "b64-img"),
                width: 60,
                alignment: "right",
              } : { text: "", width: 60 }
            ]]
          }
        },
        {
          stack: [
            updatedDate ? {
              text: updatedDate,
              margin: [0, 0, 0, 5],
              fontSize: 12
            } : { text: "" },
            summary ? {
              text: `Re: ${fmt.transform(summary)}`,
              fontSize: 12,
              bold: true
            } : { text: "" }
          ],
          alignment: "right"
        }
      ]]
    }
  });

  // Summary and description content (matching NewReportPDF2.getSummaryAndDescriptionContent)
  if (sent_at) {
    content.push({
      text: `Published: ${fmt.transform(sent_at, "date")}`,
      fontSize: 10,
      color: '#4A90E2',
      margin: [0, 10, 0, 0]
    });
  }

  if (description) {
    content.push({
      text: fmt.transform(description),
      fontSize: 11,
      color: '#555555',
      margin: [0, 10, 0, 20],
      lineHeight: 1.5,
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true
    });
  }

  // Images content (matching NewReportPDF.getImagesContent layout)
  if (images && images.length > 0) {
    const processedImages = await Promise.all(
      images.map(async ({ name: text, picture }) => {
        const imageDataUrl = await picture.dataurl();
        const processedImage = await this.processImage(imageDataUrl);
        return { text: fmt.transform(text), image: processedImage };
      })
    );

    // Group images in rows of 3 (chunk function equivalent)
    const imageRows = processedImages.reduce((memo, item, idx) => {
      if (idx % 3 === 0) {
        memo.push([]);
      }
      memo[memo.length - 1].push(item);
      return memo;
    }, [] as typeof processedImages[0][][]);

    imageRows.forEach((row) => {
      const imageRow = [{ text: "" }]; // left spacing
      for (let i = 0; i < 3; i++) {
        if (i < row.length) {
          imageRow.push({
            stack: [
              { 
                image: fmt.transform(row[i].image, "b64-img"), 
                fit: [160, 160], 
                alignment: "center", 
                width: 160 
              },
              { 
                text: row[i].text, 
                style: "a_center", 
                width: 160, 
                alignment: "center", 
                noWrap: false, 
                margin: [0, 8, 0, 0] 
              }
            ],
            width: 160,
            alignment: "center",
          } as any); // pdfmake expects cell objects, so cast as any to avoid TS error
        } else {
          imageRow.push({ text: "" });
        }
      }
      imageRow.push({ text: "" }); // right spacing
      content.push({
        layout: "noBorders",
        margin: [0, 0, 0, 10],
        unbreakable: true,
        style: {
          fontSize: 8
        },
        table: {
          body: [imageRow],
          widths: ["*", 160, 160, 160, "*"], // flexible columns for spacing
        }
      });
    });
  }

  // Close message content (matching NewReportPDF.getCloseMessageContent)
  if (close_message) {
    content.push({
      text: fmt.transform(close_message),
      fontSize: 11,
      lineHeight: 1.5,
      color: '#4A5568',
      margin: [0, 10, 0, 10],
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true,
    });
  }

  // Signing provider content (matching NewReportPDF.getSigningProviderContent)
    const processedProviderSignature = providerSignature ? await this.processImage(providerSignature) : null;
    const providerName = `${fmt.transform(provider_first_name)} ${fmt.transform(provider_last_name)}`.trim();
    const signatureDate = (report as any)?.sent_at ? fmt.transform((report as any).sent_at, "date") : "";

    const placeholderImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

    content.push({
      unbreakable: true,
      style: {
        fontSize: 9
      },
      table: {
        widths: [150, 50, 150],
        body: [
          [
            {
              stack: [
                {
                  image: processedProviderSignature ? fmt.transform(processedProviderSignature, "b64-img") : placeholderImage,
                  fit: processedProviderSignature ? [150, 75] : [100, 50],
                  alignment: "left",
                },
                {
                  text: providerName,
                  alignment: "left",
                  margin: [0, 5, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              stack: [
                {
                  text: signatureDate,
                  alignment: "left",
                  margin: [0, 80, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
          ],
          [
            {
              text: "Doctor Signature Line",
              alignment: "left",
              border: [false, true, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "Doctor Signature Date",
              alignment: "left",
              border: [false, true, false, false],
            }
          ]
        ]
      }
    });

  return content;
}

async createThreeTemplate(report: Report) {
  const { fmt } = this;
  const content = [];
  
  const {
    id,
    close_message,
    description,
    images,
    patient: {
      first_name,
      last_name,
      birth_date,
      mugshot
    },
    practice: {
      picture: practicePicture = "",
      name,
      location: {
        street1,
        street2,
        city,
        state,
        zip_code,
      },
      phone,
    },
    sent_at,
    signing_provider: {
      id: provider_id,
      signature: providerSignature,
      first_name: provider_first_name,
      last_name: provider_last_name,
    },
    summary,
  } = report;

  // Safely get updated_at if present
  const updated_at = (report as any).updated_at || null;
  
  // Process images
  const processedPracticePicture = practicePicture ? await this.processImage(practicePicture) : practicePicture;
  const processedMugshot = mugshot ? await this.processImage(mugshot) : mugshot;
  
  // Header content (matching NewReportPDF3.getHeaderContent)
  content.push({
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0
    },
    table: {
      body: [[
        processedPracticePicture ? {
          image: fmt.transform(processedPracticePicture, "b64-img"),
          width: 130,
          alignment: 'right',
          margin: [0, 0, 0, 10],
          absolutePosition: { x: 0, y: 35 }
        } : {
          text: ' ',  // Empty space instead of empty string
          margin: [0, 10, 0, 10]
        }
      ]],
      widths: ['*'],
      heights: () => {
        return 20; // set row height to 20px
      }
    },
    fillColor: '#f1f1f1',
    margin: [-40, 10, -40, 20]
  });

  // Patient content section (matching NewReportPDF3.getPatientContent)
  const patientName = `${fmt.transform(first_name)} ${fmt.transform(last_name)}`.trim();
  const dobFormatted = birth_date ? fmt.transform(birth_date, "date") : "N/A";
  
  content.push({
    layout: "noBorders",
    margin: [0, 5, 0, 2],
    table: {
      widths: ["auto", "auto"],
      body: [[
        {
          stack: [
            { text: patientName, bold: true, fontSize: 14, margin: [0, 0, 0, 5] },
            { text: `DOB: ${dobFormatted}` }
          ],
          margin: [0, 0, 10, 0],
        },
        processedMugshot
          ? { 
              image: fmt.transform(processedMugshot, "b64-img"), 
              width: 60, 
              alignment: "right" 
            }
          : { text: "", width: 60 }
      ]]
    }
  });

  // Summary and description content (matching NewReportPDF3.getSummaryAndDescriptionContent)
  if (sent_at) {
    content.push({
      text: `Published: ${fmt.transform(sent_at, "date")}`,
      fontSize: 10,
      color: '#4A90E2',
      margin: [
        0,
        0,
        0,
        10
      ]
    });
  }

  if (sent_at) {
    content.push({
      text: fmt.transform(sent_at, "date"),
      fontSize: 12,
      color: '#333333',
      margin: [
        0,
        (!mugshot && !sent_at) ? 30 : 0,
        0,
        5
      ]
    });
  }

  if (summary) {
    content.push({
      text: `Re: ${fmt.transform(summary)}`,
      fontSize: 12,
      bold: true,
      color: '#333333',
      margin: [0, 0, 0, 10]
    });
  }

  if (description) {
    content.push({
      text: fmt.transform(description),
      fontSize: 11,
      color: '#555555',
      margin: [0, 0, 0, 20],
      lineHeight: 1.5,
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true
    });
  }

  // Images content (matching NewReportPDF3.getImagesContent layout)
  if (images && images.length > 0) {
    const processedImages = await Promise.all(
      images.map(async ({ name: text, picture }) => {
        const imageDataUrl = await picture.dataurl();
        const processedImage = await this.processImage(imageDataUrl);
        return { text: fmt.transform(text), image: processedImage };
      })
    );

    // Group images in rows of 3 (chunk function equivalent)
    const imageRows = processedImages.reduce((memo, item, idx) => {
      if (idx % 3 === 0) {
        memo.push([]);
      }
      memo[memo.length - 1].push(item);
      return memo;
    }, [] as typeof processedImages[0][][]);

    imageRows.forEach((row) => {
      const imageRow = [{ text: "" }]; // left spacing
      for (let i = 0; i < 3; i++) {
        if (i < row.length) {
          imageRow.push({
            stack: [
              { 
                image: fmt.transform(row[i].image, "b64-img"), 
                fit: [160, 160], 
                alignment: "center", 
                width: 160 
              },
              { 
                text: row[i].text, 
                style: "a_center", 
                width: 160, 
                alignment: "center", 
                noWrap: false, 
                margin: [0, 8, 0, 0],
                color: "#3b3b3b",
                bold: false
              }
            ],
            width: 160,
            alignment: "center",
          } as any);
        } else {
          imageRow.push({ text: "" });
        }
      }
      imageRow.push({ text: "" }); // right spacing
      content.push({
        layout: "noBorders",
        margin: [0, 0, 0, 10],
        unbreakable: true,
        style: {
          fontSize: 8
        },
        table: {
          body: [imageRow],
          widths: ["*", 160, 160, 160, "*"], // flexible columns for spacing
        }
      });
    });
  }

  // Close message content (matching NewReportPDF3.getCloseMessageContent)
  if (close_message) {
    content.push({
      text: fmt.transform(close_message),
      fontSize: 11,
      lineHeight: 1.5,
      color: '#4A5568',
      margin: [0, 10, 0, 10],
      preserveLeadingSpaces: true,
      preserveTrailingSpaces: true,
    });
  }

  // Signing provider content (matching NewReportPDF3.getSigningProviderContent)
    const processedProviderSignature = providerSignature ? await this.processImage(providerSignature) : null;
    const providerName = `${fmt.transform(provider_first_name)} ${fmt.transform(provider_last_name)}`.trim();
    const signatureDate = (report as any)?.sent_at ? fmt.transform((report as any).sent_at, "date") : "";

    const placeholderImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

    content.push({
      unbreakable: true,
      style: { fontSize: 9 },
      table: {
        widths: [150, 50, 150],
        body: [
          [
            {
              stack: [
                {
                  image: processedProviderSignature ? fmt.transform(processedProviderSignature, "b64-img") : placeholderImage,
                  fit: processedProviderSignature ? [150, 75] : [100, 50],
                  alignment: "left",
                },
                {
                  text: providerName,
                  alignment: "left",
                  margin: [0, 5, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
            { text: "", border: [false, false, false, false] },
            {
              stack: [
                {
                  text: signatureDate,
                  alignment: "left",
                  margin: [0, 80, 0, 0],
                  fontSize: 11,
                }
              ],
              border: [false, false, false, false],
            },
          ],
          [
            {
              text: "Doctor Signature Line",
              alignment: "left",
              border: [false, true, false, false],
            },
            {
              text: "",
              border: [false, false, false, false],
            },
            {
              text: "Doctor Signature Date",
              alignment: "left",
              border: [false, true, false, false],
            }
          ]
        ]
      }
    });

  // Add footer definition (matching NewReportPDF3.build footer)
  // Note: This would typically be added to the document definition, not content
  // But including it as a comment for reference
  /*
  Footer structure from NewReportPDF3:
  {
    stack: [
      {
        canvas: [
          {
            type: 'rect',
            x: 0,
            y: 0,
            w: 595,
            h: 30,
            color: '#f1f1f1',
            lineWidth: 0,
          }
        ]
      },
      {
        layout: 'noBorders',
        margin: [40, -25, 40, 0],
        table: {
          widths: ['*'],
          body: [[
            {
              text: practice address and phone info,
              style: "bodyText",
              color: '#2B6CB0',
              alignment: "center"
            }
          ]]
        }
      }
    ]
  }
  */

  return content;
}

// Your image processing method
async processImage(imageData: string): Promise<string> {
  // Keep your existing BMP to PNG conversion logic
  if (imageData && typeof imageData === 'string' && (
      imageData.startsWith('data:image/bmp;base64,') || 
      imageData.includes('.bmp;base64,') ||
      imageData.toLowerCase().endsWith('.bmp') ||
      (imageData.includes('data:') && imageData.includes('bmp'))
    )) {
    try {
      return await this.convertBmpToPng(imageData);
    } catch (error) {
      return imageData;
    }
  }
  return imageData;
}

  trackById(_index: number, item: any) {
    return item.id
  }
}
